import { c as _c } from "react-compiler-runtime";
import { createContext, forwardRef, useContext, useMemo } from "react";
import { FieldValues, Path, useController } from "react-hook-form";
import { ErrorMessage, HintMessage, Label, useFieldIds } from "@/components/form";
import { Textarea, type TextareaProps } from "@/components/ui/textarea";
import { cn } from "@/core/utils";
type TextAreaFieldContextValue = {
  textareaId: string;
  labelId: string;
  hintId: string;
  errorId: string;
};
const TextAreaFieldContext = createContext<TextAreaFieldContextValue | null>(null);
function useTextAreaFieldContext() {
  const ctx = useContext(TextAreaFieldContext);
  if (!ctx) {
    throw new globalThis.Error("TextAreaField components must be used within TextAreaField.Root");
  }
  return ctx;
}
type TextAreaLabelProps = {
  text: string;
};
function TextAreaLabel(t0) {
  const $ = _c(4);
  const {
    text
  } = t0;
  const {
    textareaId,
    labelId
  } = useTextAreaFieldContext();
  let t1;
  if ($[0] !== labelId || $[1] !== text || $[2] !== textareaId) {
    t1 = <Label htmlFor={textareaId} id={labelId} text={text} />;
    $[0] = labelId;
    $[1] = text;
    $[2] = textareaId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
type TextAreaHintProps = {
  text: string;
};
function TextAreaHint(t0) {
  const $ = _c(3);
  const {
    text
  } = t0;
  const {
    hintId
  } = useTextAreaFieldContext();
  let t1;
  if ($[0] !== hintId || $[1] !== text) {
    t1 = <HintMessage id={hintId} text={text} />;
    $[0] = hintId;
    $[1] = text;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
type TextAreaErrorMessageProps = {
  text: string;
};
function TextAreaErrorMessage(t0) {
  const $ = _c(4);
  const {
    text
  } = t0;
  const {
    textareaId,
    errorId
  } = useTextAreaFieldContext();
  let t1;
  if ($[0] !== errorId || $[1] !== text || $[2] !== textareaId) {
    t1 = <ErrorMessage htmlFor={textareaId} id={errorId} text={text} />;
    $[0] = errorId;
    $[1] = text;
    $[2] = textareaId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
type RootProps = {
  children: React.ReactNode;
  name: string;
};
function Root(t0) {
  const $ = _c(10);
  const {
    children,
    name
  } = t0;
  const fieldIds = useFieldIds(name);
  let t1;
  if ($[0] !== fieldIds.errorId || $[1] !== fieldIds.hintId || $[2] !== fieldIds.inputId || $[3] !== fieldIds.labelId) {
    t1 = {
      textareaId: fieldIds.inputId,
      labelId: fieldIds.labelId,
      hintId: fieldIds.hintId,
      errorId: fieldIds.errorId
    };
    $[0] = fieldIds.errorId;
    $[1] = fieldIds.hintId;
    $[2] = fieldIds.inputId;
    $[3] = fieldIds.labelId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const value = t1;
  let t2;
  if ($[5] !== children) {
    t2 = <div className="flex flex-col gap-1 text-left">{children}</div>;
    $[5] = children;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t2 || $[8] !== value) {
    t3 = <TextAreaFieldContext.Provider value={value}>{t2}</TextAreaFieldContext.Provider>;
    $[7] = t2;
    $[8] = value;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
const TextArea = forwardRef<HTMLTextAreaElement, TextareaProps>(function TextArea(props, ref) {
  const $ = _c(19);
  const {
    errorId,
    hintId,
    textareaId,
    labelId
  } = useTextAreaFieldContext();
  let className;
  let onBlur;
  let onChange;
  let rest;
  if ($[0] !== props) {
    const {
      id: _id,
      className: t0,
      onBlur: t1,
      onChange: t2,
      ...t3
    } = props;
    className = t0;
    onBlur = t1;
    onChange = t2;
    rest = t3;
    $[0] = props;
    $[1] = className;
    $[2] = onBlur;
    $[3] = onChange;
    $[4] = rest;
  } else {
    className = $[1];
    onBlur = $[2];
    onChange = $[3];
    rest = $[4];
  }
  const invalid = !!props["aria-invalid"];
  const t0 = `${hintId} ${errorId}`;
  const t1 = props["aria-errormessage"];
  const t2 = invalid || undefined;
  const t3 = invalid && "border-red-600 ring-1 ring-red-600 placeholder:text-red-600";
  let t4;
  if ($[5] !== className || $[6] !== t3) {
    t4 = cn(t3, className);
    $[5] = className;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== labelId || $[9] !== onBlur || $[10] !== onChange || $[11] !== ref || $[12] !== rest || $[13] !== t0 || $[14] !== t1 || $[15] !== t2 || $[16] !== t4 || $[17] !== textareaId) {
    t5 = <Textarea {...rest} aria-describedby={t0} aria-errormessage={t1} aria-invalid={t2} aria-labelledby={labelId} className={t4} id={textareaId} ref={ref} onBlur={onBlur} onChange={onChange} />;
    $[8] = labelId;
    $[9] = onBlur;
    $[10] = onChange;
    $[11] = ref;
    $[12] = rest;
    $[13] = t0;
    $[14] = t1;
    $[15] = t2;
    $[16] = t4;
    $[17] = textareaId;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
});
TextArea.displayName = "TextAreaField.TextArea";
type NativeProps = {
  children: React.ReactNode;
  label: string;
  hint?: string;
  textareaProps: TextareaProps;
  name: string;
};
function Native(t0) {
  const $ = _c(11);
  const {
    label,
    hint,
    textareaProps,
    name
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <TextAreaLabel text={label} />;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== textareaProps) {
    t2 = <TextArea {...textareaProps} />;
    $[2] = textareaProps;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== hint) {
    t3 = hint && <TextAreaHint text={hint} />;
    $[4] = hint;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== name || $[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <Root name={name}>{t1}{t2}{t3}</Root>;
    $[6] = name;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
type RHF<T extends FieldValues> = {
  name: Path<T>;
  label: string;
  hint?: string;
  textareaProps?: Omit<TextareaProps, "id" | "name" | "value" | "defaultValue" | "aria-invalid" | "aria-errormessage">;
};
function RHF(t0) {
  const $ = _c(36);
  const {
    name,
    label,
    hint,
    textareaProps
  } = t0;
  let t1;
  if ($[0] !== name) {
    t1 = {
      name
    };
    $[0] = name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    field,
    fieldState
  } = useController(t1);
  let t2;
  if ($[2] !== textareaProps) {
    t2 = textareaProps ?? {};
    $[2] = textareaProps;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let onBlurProp;
  let onChangeProp;
  let restTextareaProps;
  if ($[4] !== t2) {
    ({
      onChange: onChangeProp,
      onBlur: onBlurProp,
      ...restTextareaProps
    } = t2);
    $[4] = t2;
    $[5] = onBlurProp;
    $[6] = onChangeProp;
    $[7] = restTextareaProps;
  } else {
    onBlurProp = $[5];
    onChangeProp = $[6];
    restTextareaProps = $[7];
  }
  let t3;
  if ($[8] !== field || $[9] !== onChangeProp) {
    t3 = e => {
      onChangeProp?.(e);
      field.onChange(e);
    };
    $[8] = field;
    $[9] = onChangeProp;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleChange = t3;
  let t4;
  if ($[11] !== field || $[12] !== onBlurProp) {
    t4 = e_0 => {
      onBlurProp?.(e_0);
      field.onBlur();
    };
    $[11] = field;
    $[12] = onBlurProp;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleBlur = t4;
  let t5;
  if ($[14] !== label) {
    t5 = <TextAreaLabel text={label} />;
    $[14] = label;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const t6 = fieldState.error?.message;
  const t7 = !!fieldState.error?.message;
  const t8 = typeof field.value === "string" ? field.value : field.value ?? "";
  let t9;
  if ($[16] !== field.name || $[17] !== handleBlur || $[18] !== handleChange || $[19] !== restTextareaProps || $[20] !== t6 || $[21] !== t7 || $[22] !== t8) {
    t9 = <TextArea aria-errormessage={t6} aria-invalid={t7} {...restTextareaProps} name={field.name} value={t8} onBlur={handleBlur} onChange={handleChange} />;
    $[16] = field.name;
    $[17] = handleBlur;
    $[18] = handleChange;
    $[19] = restTextareaProps;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== hint) {
    t10 = hint && <TextAreaHint text={hint} />;
    $[24] = hint;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== fieldState.error) {
    t11 = fieldState.error?.message && <TextAreaErrorMessage text={fieldState.error.message} />;
    $[26] = fieldState.error;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t11) {
    t12 = <div className="h-2">{t11}</div>;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== name || $[31] !== t10 || $[32] !== t12 || $[33] !== t5 || $[34] !== t9) {
    t13 = <Root name={name}>{t5}{t9}{t10}{t12}</Root>;
    $[30] = name;
    $[31] = t10;
    $[32] = t12;
    $[33] = t5;
    $[34] = t9;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  return t13;
}
export const TextAreaField = {
  RHF,
  Native
};